import React, { ReactElement, useMemo, useState } from 'react';
import User from '@westwing/ui-kit/Icons/User';
import styled from 'styled-components';
import { underTablet } from '@westwing/ui-kit/breakPoints';
import AccountMenu from '@westwing/ui-kit/ContentFlyout/AccountMenu';
import ContentFlyout from '@westwing/ui-kit/ContentFlyout';
import { useClubContext } from '../../hooks/ClubContext';
import { ClubLink } from '../../types/clubLinksTypes';
import HeaderIconLink from 'Components/IconLink';
import { ClubPOKey, useHeaderContext } from 'Components/HeaderContext';

const StyledHeaderIconLink = styled(HeaderIconLink)`
    ${underTablet`
        display: none;
  `}
`;

interface AccountFlyoutProps {
    onClickWhenLoginRequired: (evt: React.MouseEvent) => void;
}

const items: { title: string; pathName: ClubLink; testId: string; loginRequired?: boolean }[] = [
    {
        title: 'OwwHeader_My orders',
        pathName: ClubLink.CUSTOMER_ORDER,
        testId: 'oww-header-account-menu-orders',
    },
    {
        title: 'OwwHeader_My Credentials',
        pathName: ClubLink.CUSTOMER_ACCOUNT,
        testId: 'oww-header-account-menu-account',
    },
    {
        title: 'OwwHeader_My Newsletters',
        pathName: ClubLink.CUSTOMER_NEWSLETTER,
        testId: 'oww-header-account-menu-newsletter',
    },
    {
        title: 'OwwHeader_My coupons',
        pathName: ClubLink.CUSTOMER_COUPON,
        testId: 'oww-header-account-menu-coupon',
    },
    {
        title: 'OwwHeader_My Notifications',
        pathName: ClubLink.CUSTOMER_NOTIFICATIONS,
        testId: 'oww-header-account-menu-notification',
    },
    {
        title: 'OwwHeader_Invite friends now',
        pathName: ClubLink.CUSTOMER_ACCOUNT_CREATE_INVITATION,
        testId: 'oww-header-account-menu-invitation',
    },
    {
        title: 'OwwHeader_Help & Contact',
        pathName: ClubLink.HELPCENTER,
        testId: 'oww-header-account-menu-helpcenter',
    },
    {
        title: 'OwwHeader_Logout',
        pathName: ClubLink.LOGOUT,
        testId: 'oww-header-logout',
        loginRequired: false,
    },
];

const AccountFlyout = ({ onClickWhenLoginRequired }: AccountFlyoutProps): ReactElement => {
    const [isActive, setIsActive] = useState(false);
    const { t } = useHeaderContext();
    const { links } = useClubContext();
    const menuSection = useMemo(() => {
        const handleClick = (loginRequired: boolean) => (evt: React.MouseEvent) => {
            if (loginRequired) {
                onClickWhenLoginRequired(evt);
            }
        };
        return [
            {
                key: 'account-menu',
                title: '',
                items: items.map(item => ({
                    ...item,
                    title: t(item.title as unknown as ClubPOKey),
                    url: links[item.pathName].path,
                    itemClick: handleClick(item.loginRequired !== false),
                })),
            },
        ];
    }, [t, links, onClickWhenLoginRequired]);

    const handleMenuOpen = () => {
        setIsActive(true);
    };

    const handleMenuClose = () => {
        setIsActive(false);
    };

    return (
        <div onMouseEnter={handleMenuOpen} onMouseLeave={handleMenuClose}>
            <StyledHeaderIconLink
                href={links[ClubLink.CUSTOMER_ACCOUNT].path}
                onClick={onClickWhenLoginRequired}
                data-testid="oww-header-account-icon"
                aria-label={t('OwwHeader_My account')}
            >
                <User />
            </StyledHeaderIconLink>
            <ContentFlyout
                rightPosition={-3}
                isActive={isActive}
                onOverlayClose={handleMenuClose}
                propagateOutsideClicks
            >
                <AccountMenu menuSections={menuSection} />
            </ContentFlyout>
        </div>
    );
};

export default AccountFlyout;
